// Geboortevenster · Stap 1 van de afleiding: venstersegmenten → grensmomenten → activatieset per interval (en de oorzaken van één wissel).
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { HdBodygraphConfig, HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { getHdPlanetDisplayConfigByPlanetId } from '../bodygraph/planet-grid/hd-planet-display-config';
import { HD_GLOBAL_SHIFT_MS } from '../hd-global-shift';
import {
  HdBirthWindowBodySegments,
  HdBirthWindowChange,
  HdBirthWindowData,
  HdBirthWindowInterval,
  HdBirthWindowSegment,
} from './birth-window.models';

/**
 * Van activatie-segmenten (per lichaam en kant) naar activatiesets per
 * tijdsinterval (spec §2.4). Puur; geen Angular.
 */

/** Alle grensmomenten in het venster, gesorteerd en ontdubbeld, inclusief begin en einde. */
export function collectBirthWindowBoundaries(data: HdBirthWindowData): number[] {
  const set = new Set<number>([data.fromMs, data.toMs]);
  // De globale shift (15-02-2027) is zelf een grensmoment: erna breekt 19/49
  // (hd-global-shift.ts), dus eigenschappen kunnen daar wisselen zonder dat
  // een planeet van cel wisselt.
  if (HD_GLOBAL_SHIFT_MS > data.fromMs && HD_GLOBAL_SHIFT_MS < data.toMs) set.add(HD_GLOBAL_SHIFT_MS);
  for (const body of data.bodies) {
    for (const segment of body.segments) {
      if (segment.fromMs > data.fromMs && segment.fromMs < data.toMs) set.add(segment.fromMs);
    }
  }
  return [...set].sort((a, b) => a - b);
}

/** Het segment van een lichaam dat `ms` bevat (laatste segment ook op `toMs`); undefined buiten het venster. */
export function segmentAt(body: HdBirthWindowBodySegments, ms: number): HdBirthWindowSegment | undefined {
  const segments = body.segments;
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    const last = i === segments.length - 1;
    if (ms >= segment.fromMs && (ms < segment.toMs || (last && ms <= segment.toMs))) return segment;
  }
  return undefined;
}

function toPlanetaryActivation(
  body: HdBirthWindowBodySegments,
  segment: HdBirthWindowSegment,
  postShiftBirth: boolean,
): HdBodygraphPlanetaryActivation {
  const display = getHdPlanetDisplayConfigByPlanetId(body.planetId);
  return {
    ...(postShiftBirth ? { postShiftBirth: true as const } : {}),
    id: `bw:${body.side}:${body.sourceKey}`,
    gate: segment.gate,
    side: body.side,
    sourceType: 'planet',
    sourceKey: body.sourceKey,
    planet: display?.label ?? body.sourceKey,
    planetId: body.planetId,
    line: segment.line,
    color: segment.color ?? 0,
    tone: segment.tone ?? 0,
    base: segment.base ?? 0,
    longitude: 0,
  };
}

/** De activatieset op één moment (bv. voor de chart-preview tijdens het slepen). */
export function birthWindowActivationsAt(data: HdBirthWindowData, ms: number): HdBodygraphPlanetaryActivation[] {
  const result: HdBodygraphPlanetaryActivation[] = [];
  const postShiftBirth = ms >= HD_GLOBAL_SHIFT_MS;
  for (const body of data.bodies) {
    const segment = segmentAt(body, ms);
    if (segment) result.push(toPlanetaryActivation(body, segment, postShiftBirth));
  }
  return result;
}

/** Intervallen tussen opeenvolgende grensmomenten, elk met de activatieset die daar geldt. */
export function buildBirthWindowIntervals(data: HdBirthWindowData): HdBirthWindowInterval[] {
  const boundaries = collectBirthWindowBoundaries(data);
  const intervals: HdBirthWindowInterval[] = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const fromMs = boundaries[i];
    const toMs = boundaries[i + 1];
    intervals.push({ fromMs, toMs, activations: birthWindowActivationsAt(data, fromMs) });
  }
  return intervals;
}

/** Welke lichamen op precies dit grensmoment van cel wisselen. */
export function birthWindowChangesAt(data: HdBirthWindowData, ms: number): HdBirthWindowChange[] {
  const changes: HdBirthWindowChange[] = [];
  for (const body of data.bodies) {
    const segments = body.segments;
    for (let i = 1; i < segments.length; i++) {
      if (segments[i].fromMs === ms) {
        changes.push({ side: body.side, planetId: body.planetId, sourceKey: body.sourceKey, from: segments[i - 1], to: segments[i] });
      }
    }
  }
  return changes;
}

/** Bodygraph-configuratie (enkel poorten + kant) uit een activatieset. */
export function toBirthWindowConfig(activations: readonly HdBodygraphPlanetaryActivation[]): HdBodygraphConfig {
  return {
    schemaVersion: 1,
    activations: activations.map(activation => ({
      id: activation.id,
      gate: activation.gate,
      side: activation.side,
      sourceType: activation.sourceType,
      sourceKey: activation.sourceKey,
      ...(activation.postShiftBirth ? { postShiftBirth: true as const } : {}),
    })),
  };
}
